import type { Message } from '../types';

export type ConversationKey = readonly ['server', string, string] | readonly ['dm', string];
export type MessageCacheInvalidation = 'purge' | 'storage';
export type CachedMessage = Omit<Message, '_pending' | '_failed' | '_clientMessageId'>;

export interface CachedWindow {
  key: ConversationKey;
  parentChannelId?: string;
  messages: CachedMessage[];
  hasMoreOlder: boolean;
  fetchedAt: number;
  lastUsedAt: number;
  utf8Bytes: number;
}

export type WindowInput = Pick<CachedWindow, 'key' | 'parentChannelId' | 'hasMoreOlder' | 'fetchedAt'> & {
  messages: readonly Message[];
};

export interface MessageCache {
  readWindow: (key: ConversationKey) => Promise<CachedWindow | null>;
  writeWindow: (input: WindowInput, isCurrent?: () => boolean) => Promise<void>;
  removeWindows: (channelIds: readonly string[]) => Promise<void>;
  cancelWrites: (key: ConversationKey) => void;
  close: () => void;
  clearAccount: () => Promise<void>;
}

export const MESSAGE_CACHE_LIMITS = {
  messages: 100,
  windowBytes: 1024 * 1024,
  windows: 100,
  accountBytes: 20 * 1024 * 1024,
  maxAgeMs: 7 * 24 * 60 * 60 * 1000,
  deadlineMs: 500,
} as const;

const STORE = 'channel_windows';
const DATABASE_VERSION = 2;
const METADATA_INDEX = 'eviction';
const PARENT_INDEX = 'parentChannelId';
const CHANNEL = 'borgee-message-cache-lifecycle';
const contentTypes = new Set(['text', 'image', 'command', 'task', 'tool_card', 'epoch']);
const encoder = new TextEncoder();
const channelId = (key: ConversationKey): string => key[key.length - 1];
const bytes = (messages: CachedMessage[], hasMoreOlder: boolean): number =>
  encoder.encode(JSON.stringify({ messages, hasMoreOlder })).byteLength;

export const messageCacheDatabaseName = (apiOrigin: string, userId: string): string =>
  `borgee-message-cache:${JSON.stringify([new URL(apiOrigin).origin, userId])}`;

export const projectCachedMessage = (message: Message): CachedMessage | null => {
  if (message._pending || message._failed || message.id.startsWith('local-') || message.deleted_at) return null;
  const {
    id, channel_id, sender_id, sender_name, content, content_type, type,
    reply_to_id, created_at, edited_at, deleted_at, mentions, reactions, attachments, quick_action,
  } = message;
  return {
    id, channel_id, sender_id, sender_name, content, content_type, type,
    reply_to_id, created_at, edited_at, deleted_at,
    mentions: mentions?.slice(),
    reactions: reactions?.map(({ emoji, count, user_ids }) => ({ emoji, count, user_ids: user_ids.slice() })),
    attachments: attachments?.map(({ url, filename, content_type, size_bytes }) => ({ url, filename, content_type, size_bytes })),
    quick_action,
  };
};

export const prepareCachedWindow = (input: WindowInput, now = Date.now()): CachedWindow | null => {
  const rows = new Map<string, CachedMessage>();
  for (const message of input.messages) {
    const row = projectCachedMessage(message);
    if (row && row.channel_id !== channelId(input.key)) throw new TypeError('Message does not belong to cache window');
    if (row) rows.set(row.id, row);
  }
  let messages = [...rows.values()].sort((a, b) => a.created_at - b.created_at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  let hasMoreOlder = input.hasMoreOlder;
  if (messages.length > MESSAGE_CACHE_LIMITS.messages) {
    messages = messages.slice(-MESSAGE_CACHE_LIMITS.messages);
    hasMoreOlder = true;
  }
  let start = messages.length;
  let messageBytes = 0;
  let utf8Bytes = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    const candidateBytes = messageBytes + encoder.encode(JSON.stringify(messages[i])).byteLength;
    const candidateHasMore = hasMoreOlder || i > 0;
    const commas = messages.length - i - 1;
    const windowBytes = bytes([], candidateHasMore) + candidateBytes + commas;
    if (windowBytes > MESSAGE_CACHE_LIMITS.windowBytes) break;
    start = i;
    messageBytes = candidateBytes;
    utf8Bytes = windowBytes;
  }
  if (start === messages.length) return null;
  hasMoreOlder ||= start > 0;
  messages = messages.slice(start);
  return { key: [...input.key] as ConversationKey, parentChannelId: input.parentChannelId, messages, hasMoreOlder, fetchedAt: input.fetchedAt, lastUsedAt: now, utf8Bytes };
};

const record = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object';
const strings = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === 'string');
const optional = (value: unknown, check: (value: unknown) => boolean): boolean => value === undefined || check(value);
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const nullableString = (value: unknown): boolean => value === null || typeof value === 'string';
const nullableNumber = (value: unknown): boolean => value === null || finite(value);
const validMessage = (value: unknown): value is Message => {
  if (!record(value)) return false;
  return ['id', 'channel_id', 'sender_id', 'content'].every((key) => typeof value[key] === 'string') &&
    typeof value.content_type === 'string' && contentTypes.has(value.content_type) &&
    nullableString(value.reply_to_id) && finite(value.created_at) && nullableNumber(value.edited_at) &&
    optional(value.deleted_at, nullableNumber) && !value.deleted_at &&
    optional(value.sender_name, (v) => typeof v === 'string') && optional(value.type, (v) => typeof v === 'string') &&
    optional(value.quick_action, nullableString) && optional(value.mentions, strings) &&
    optional(value.reactions, (v) => Array.isArray(v) && v.every((r) => record(r) && typeof r.emoji === 'string' && finite(r.count) && strings(r.user_ids))) &&
    optional(value.attachments, (v) => Array.isArray(v) && v.every((a) => record(a) && typeof a.url === 'string' && typeof a.filename === 'string' && typeof a.content_type === 'string' && optional(a.size_bytes, finite)));
};

const validWindow = (value: unknown, now: number): value is CachedWindow => {
  if (!record(value)) return false;
  const key = value.key;
  if (!Array.isArray(key) ||
    !(key[0] === 'dm' && key.length === 2 || key[0] === 'server' && key.length === 3) ||
    !strings(key) || !Array.isArray(value.messages) || !value.messages.length ||
    value.messages.length > MESSAGE_CACHE_LIMITS.messages || !value.messages.every(validMessage) ||
    value.messages.some((message) => message.channel_id !== key[key.length - 1]) ||
    typeof value.hasMoreOlder !== 'boolean' || !finite(value.fetchedAt) || value.fetchedAt > now ||
    now - value.fetchedAt > MESSAGE_CACHE_LIMITS.maxAgeMs || !finite(value.lastUsedAt) ||
    !optional(value.parentChannelId, (v) => typeof v === 'string')) return false;
  const normalized = prepareCachedWindow(value as unknown as WindowInput, now);
  return normalized !== null && normalized.messages.length === value.messages.length &&
    normalized.utf8Bytes === value.utf8Bytes && normalized.utf8Bytes <= MESSAGE_CACHE_LIMITS.windowBytes;
};

const aborted = (): DOMException => new DOMException('Cache account binding closed', 'AbortError');
const errorName = (error: unknown): string => error instanceof Error || error instanceof DOMException ? error.name : 'UnknownError';
const warnings = new Set<string>();
const warn = (operation: string, error: unknown): void => {
  const category = errorName(error);
  if (category === 'AbortError' || warnings.has(category)) return;
  warnings.add(category);
  console.warn('message-cache', { component: 'message-cache', operation, category });
};

export const createMessageCache = ({ apiOrigin, userId, onInvalidated }: {
  apiOrigin: string;
  userId: string;
  onInvalidated?: (reason: MessageCacheInvalidation) => void;
}): MessageCache => {
  const name = messageCacheDatabaseName(apiOrigin, userId);
  let closed = false;
  let writesDisabled = false;
  let database: IDBDatabase | undefined;
  let opening: Promise<IDBDatabase> | undefined;
  const transactions = new Set<IDBTransaction>();
  const cancelOpens = new Set<() => void>();
  const writeEpochs = new Map<string, number>();
  const writeTransactions = new Map<IDBTransaction, string>();
  const openBroadcast = (): BroadcastChannel | undefined => {
    try { return typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(CHANNEL); }
    catch (error) { warn('broadcast', error); return undefined; }
  };
  const broadcast = openBroadcast();

  const close = (): void => {
    if (closed) return;
    closed = true;
    for (const cancel of cancelOpens) cancel();
    for (const tx of transactions) {
      try { tx.abort(); } catch (error) {
        if (errorName(error) !== 'InvalidStateError') warn('abort', error);
      }
    }
    database?.close();
    database = undefined;
    broadcast?.close();
  };

  const invalidate = (reason: MessageCacheInvalidation): void => {
    close();
    onInvalidated?.(reason);
  };
  if (broadcast) broadcast.onmessage = (event: MessageEvent<unknown>) => {
    if (record(event.data) && event.data.type === 'clear' && event.data.account === name) invalidate('purge');
  };

  const open = (): Promise<IDBDatabase> => {
    if (closed) return Promise.reject(aborted());
    if (database) return Promise.resolve(database);
    if (opening) return opening;
    opening = new Promise<IDBDatabase>((resolve, reject) => {
      let settled = false;
      const finish = (error?: unknown, db?: IDBDatabase): void => {
        if (settled) { db?.close(); return; }
        settled = true;
        clearTimeout(timer);
        cancelOpens.delete(cancel);
        if (error) reject(error);
        else if (db) resolve(db);
      };
      const cancel = (): void => finish(aborted());
      cancelOpens.add(cancel);
      const timer = setTimeout(() => finish(new DOMException('Cache open deadline exceeded', 'TimeoutError')), MESSAGE_CACHE_LIMITS.deadlineMs);
      try {
        if (typeof indexedDB === 'undefined') throw new DOMException('IndexedDB unavailable', 'NotSupportedError');
        const request = indexedDB.open(name, DATABASE_VERSION);
        request.onerror = () => finish(request.error);
        request.onblocked = () => finish(new DOMException('Cache database blocked', 'InvalidStateError'));
        request.onupgradeneeded = () => {
          const db = request.result;
          for (const store of Array.from(db.objectStoreNames)) db.deleteObjectStore(store);
          const store = db.createObjectStore(STORE, { keyPath: 'key' });
          store.createIndex(METADATA_INDEX, ['lastUsedAt', 'fetchedAt', 'utf8Bytes']);
          store.createIndex(PARENT_INDEX, 'parentChannelId');
        };
        request.onsuccess = () => {
          const db = request.result;
          if (closed || settled) { db.close(); finish(aborted()); return; }
          db.onversionchange = () => invalidate('storage');
          database = db;
          finish(undefined, db);
        };
      } catch (error) { finish(error); }
    }).finally(() => { opening = undefined; });
    return opening;
  };

  const transaction = async <T>(
    mode: IDBTransactionMode,
    body: (store: IDBObjectStore, setResult: (value: T) => void, guard: (work: () => void) => void) => void,
    isCurrent: () => boolean = () => true,
    writeKey?: string,
  ): Promise<T> => {
    const db = await open();
    if (closed || !isCurrent()) throw aborted();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      transactions.add(tx);
      if (writeKey !== undefined) writeTransactions.set(tx, writeKey);
      let result: T;
      let cause: unknown;
      let settled = false;
      let timer: ReturnType<typeof setTimeout>;
      const finish = (): void => {
        clearTimeout(timer);
        transactions.delete(tx);
        writeTransactions.delete(tx);
      };
      const abort = (error: unknown): void => {
        if (settled) return;
        settled = true;
        cause = error;
        finish();
        try { tx.abort(); } catch (abortError) {
          if (errorName(abortError) !== 'InvalidStateError') warn('abort', abortError);
        }
        reject(error);
      };
      const watch = (operation: 'queue' | 'request'): void => {
        clearTimeout(timer);
        timer = setTimeout(() => abort(new DOMException(`Cache ${operation} deadline exceeded`, operation === 'queue' ? 'CacheQueueTimeoutError' : 'CacheRequestTimeoutError')), MESSAGE_CACHE_LIMITS.deadlineMs);
      };
      tx.oncomplete = () => {
        finish();
        if (settled) return;
        settled = true;
        if (closed || !isCurrent()) reject(aborted());
        else resolve(result);
      };
      tx.onabort = () => { finish(); if (!settled) { settled = true; reject(cause ?? tx.error ?? aborted()); } };
      tx.onerror = () => { cause ??= tx.error; };
      const guard = (work: () => void): void => {
        if (settled) return;
        if (closed || !isCurrent()) { abort(aborted()); return; }
        watch('request');
        try { work(); } catch (error) { abort(error); }
      };
      // The first key-only request completes only after native writer admission.
      // Queue delay must not consume an active request's progress budget.
      watch('queue');
      try {
        const store = tx.objectStore(STORE);
        const admitted = store.count();
        admitted.onsuccess = () => guard(() => body(store, (value) => { result = value; }, guard));
      } catch (error) { abort(error); }
    });
  };

  const metadata = (
    store: IDBObjectStore,
    guard: (work: () => void) => void,
    accept: (entries: { key: IDBValidKey; lastUsedAt: number; utf8Bytes: number }[]) => void,
  ): void => {
    const keys = store.getAllKeys();
    keys.onsuccess = () => guard(() => {
      const unindexed = new Map(keys.result.map((key) => [JSON.stringify(key), key]));
      const entries: { key: IDBValidKey; lastUsedAt: number; utf8Bytes: number }[] = [];
      const cursor = store.index(METADATA_INDEX).openKeyCursor();
      cursor.onsuccess = () => guard(() => {
        const entry = cursor.result;
        if (!entry) {
          for (const key of unindexed.values()) store.delete(key);
          accept(entries);
          return;
        }
        const key = entry.primaryKey;
        unindexed.delete(JSON.stringify(key));
        const values = entry.key as number[];
        const [lastUsedAt, fetchedAt, utf8Bytes] = values;
        const now = Date.now();
        if (!Array.isArray(key) || !strings(key) ||
          !(key[0] === 'dm' && key.length === 2 || key[0] === 'server' && key.length === 3) ||
          !finite(lastUsedAt) || !finite(fetchedAt) || fetchedAt > now || now - fetchedAt > MESSAGE_CACHE_LIMITS.maxAgeMs ||
          !finite(utf8Bytes) || utf8Bytes <= 0 || utf8Bytes > MESSAGE_CACHE_LIMITS.windowBytes) store.delete(key);
        else entries.push({ key, lastUsedAt, utf8Bytes });
        entry.continue();
      });
    });
  };

  const write = async (input: WindowInput, isCurrent: () => boolean, recoverQuota: boolean): Promise<void> => {
    const window = prepareCachedWindow(input);
    await transaction<void>('readwrite', (store, _result, guard) => {
      metadata(store, guard, (entries) => {
        const retained: typeof entries = [];
        for (const entry of entries) {
          if (JSON.stringify(entry.key) === JSON.stringify(input.key) || recoverQuota) store.delete(entry.key);
          else retained.push(entry);
        }
        if (!window) { store.delete([...input.key]); return; }
        let totalBytes = retained.reduce((sum, entry) => sum + entry.utf8Bytes, window.utf8Bytes);
        while (retained.length + 1 > MESSAGE_CACHE_LIMITS.windows || totalBytes > MESSAGE_CACHE_LIMITS.accountBytes) {
          const evicted = retained.shift();
          if (!evicted) break;
          totalBytes -= evicted.utf8Bytes;
          store.delete(evicted.key);
        }
        store.put(window);
      });
    }, isCurrent, JSON.stringify(input.key));
  };

  return {
    close,
    cancelWrites: (key) => {
      const encoded = JSON.stringify(key);
      writeEpochs.set(encoded, (writeEpochs.get(encoded) ?? 0) + 1);
      for (const [tx, writeKey] of writeTransactions) if (writeKey === encoded) {
        try { tx.abort(); } catch (error) {
          if (errorName(error) !== 'InvalidStateError') warn('abort', error);
        }
      }
    },
    readWindow: async (key) => {
      try {
        return await transaction<CachedWindow | null>(writesDisabled ? 'readonly' : 'readwrite', (store, result, guard) => {
          const request = store.get([...key]);
          request.onsuccess = () => guard(() => {
            const value: unknown = request.result;
            if (value === undefined) { result(null); return; }
            if (!validWindow(value, Date.now())) {
              if (!writesDisabled) store.delete([...key]);
              result(null);
              return;
            }
            const window = prepareCachedWindow(value)!;
            if (!writesDisabled) store.put(window);
            result(window);
          });
        });
      } catch (error) { warn('read', error); return null; }
    },
    writeWindow: async (input, isCurrent = () => true) => {
      if (writesDisabled || closed || !isCurrent()) return;
      const encoded = JSON.stringify(input.key);
      const epoch = writeEpochs.get(encoded) ?? 0;
      const current = (): boolean => isCurrent() && epoch === (writeEpochs.get(encoded) ?? 0);
      try { await write(input, current, false); }
      catch (error) {
        if (errorName(error) !== 'QuotaExceededError') { warn('write', error); return; }
        try { await write(input, current, true); }
        catch (retryError) {
          if (errorName(retryError) === 'QuotaExceededError') writesDisabled = true;
          warn('write', retryError);
        }
      }
    },
    removeWindows: async (channelIds) => {
      try {
        await transaction<void>('readwrite', (store, _result, guard) => {
          const removed = new Set(channelIds);
          const pending = [...removed];
          const descend = (): void => {
            const parent = pending.shift();
            if (parent === undefined) {
              const keys = store.getAllKeys();
              keys.onsuccess = () => guard(() => {
                for (const key of keys.result) if (Array.isArray(key) && removed.has(key[key.length - 1] as string)) store.delete(key);
              });
              return;
            }
            const children = store.index(PARENT_INDEX).openKeyCursor(parent);
            children.onsuccess = () => guard(() => {
              const child = children.result;
              if (!child) { descend(); return; }
              const key = child.primaryKey;
              if (Array.isArray(key)) {
                const id = key[key.length - 1] as string;
                if (!removed.has(id)) { removed.add(id); pending.push(id); }
              }
              child.continue();
            });
          };
          descend();
        });
      } catch (error) { warn('remove', error); }
    },
    clearAccount: async () => {
      const publisher = closed ? openBroadcast() : broadcast;
      try { publisher?.postMessage({ type: 'clear', account: name }); }
      catch (error) { warn('broadcast', error); }
      finally { if (closed) publisher?.close(); }
      close();
      try {
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(() => reject(new DOMException('Cache removal deadline exceeded', 'TimeoutError')), MESSAGE_CACHE_LIMITS.deadlineMs);
          const request = indexedDB.deleteDatabase(name);
          request.onsuccess = () => { clearTimeout(timer); resolve(); };
          request.onerror = () => { clearTimeout(timer); reject(request.error); };
          request.onblocked = () => { clearTimeout(timer); reject(new DOMException('Cache removal blocked', 'InvalidStateError')); };
        });
      } catch (error) { warn('clear', error); }
    },
  };
};
